 // src/pages/StudentDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Bar, Line } from "react-chartjs-2";
import "chart.js/auto";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  limit as qlimit,
  onSnapshot,
} from "firebase/firestore";
import { Tabs, Tab, useMediaQuery } from "@mui/material";
import { getStorage, ref, deleteObject } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import "../App.css";

/* ───────────────────────────── helpers ───────────────────────────── */
function msFromAny(v) {
  if (!v) return null;
  if (typeof v === "number") return v > 1e12 ? v : v * 1000; // seconds → ms
  if (v?.toMillis) { try { return v.toMillis(); } catch {} } // Firestore Timestamp
  if (v?._seconds != null) return v._seconds * 1000;         // admin SDK shape
  const d = new Date(v);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}
function asDateMaybe(v) {
  const ms = msFromAny(v);
  return ms == null ? null : new Date(ms);
}
function addMonths(date, months) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() !== day) d.setDate(0); // snap back to last day of previous month
  return d;
}
function currentPeriodFromAnchor(anchorMs, now = new Date()) {
  if (!anchorMs) return null;
  const anchor = new Date(anchorMs);
  if (isNaN(anchor.getTime())) return null;

  // Find k such that start = anchor + k months <= now < end = anchor + (k+1) months
  let start = new Date(anchor);
  let end = addMonths(start, 1);
  while (now >= end) {
    start = end;
    end = addMonths(start, 1);
  }
  return { start, end }; // end is exclusive
}
function formatDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function startOfCalendarMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}
function endOfCalendarMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0); // exclusive
}
function inRange(dateStrOrObj, start, endExclusive) {
  const d = parseActivityDate(dateStrOrObj);
  if (!d) return false;
  if (start && d < start) return false;
  if (endExclusive && d >= endExclusive) return false;
  return true;
}

function parseActivityDate(dateStrOrObj) {
  if (!dateStrOrObj) return null;

  // If it's exactly YYYY-MM-DD, treat it as end-of-day LOCAL time
  // so it correctly falls "inside" a day for endExclusive window comparisons.
  if (typeof dateStrOrObj === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStrOrObj)) {
    const [y, m, d] = dateStrOrObj.split("-").map(Number);
    return new Date(y, m - 1, d, 23, 59, 59, 999);
  }

  // Otherwise keep existing behaviour
  return typeof dateStrOrObj === "string"
    ? new Date(dateStrOrObj)
    : asDateMaybe(dateStrOrObj);
}


function sumStoriesInRange(activityArr, start, endExclusive) {
  const arr = Array.isArray(activityArr) ? activityArr : [];
  return arr.reduce((sum, e) => {
    const when = e?.date || e?.ts || e?.time || null;
    const stories = Number(e?.storiesGenerated || 0);
    if (!start && !endExclusive) return sum + stories;
    return inRange(when, start, endExclusive) ? sum + stories : sum;
  }, 0);
}

function avgByWeek(scores) {
  // local helpers so we don't need global yyyymmdd/weekStartMonday
  const yyyymmddLocal = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.toISOString().slice(0, 10);
  };

  const weekStartMondayLocal = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const day = x.getDay() || 7; // Mon=1..Sun=7
    x.setDate(x.getDate() - (day - 1));
    return x;
  };

  const map = new Map(); // weekKey -> [scores]

  for (const s of scores || []) {
    const v = s?.date || s?.ts || s?.createdAt;

    const d =
      typeof v === "string"
        ? new Date(v)
        : s?.ts?.toDate?.() ||
          s?.createdAt?.toDate?.() ||
          asDateMaybe(v) ||
          new Date(v);

    const wk = yyyymmddLocal(weekStartMondayLocal(d || new Date()));

    if (!map.has(wk)) map.set(wk, []);
    map.get(wk).push(Number(s?.score || 0));
  }

  return Array.from(map.entries())
    .map(([week, arr]) => ({
      week,
      avg: Math.round(arr.reduce((a, b) => a + b, 0) / Math.max(arr.length, 1)),
    }))
    .sort((a, b) => a.week.localeCompare(b.week));
}

/* ───────────────────────────── component ───────────────────────────── */
const StudentDashboard = () => {
  const auth = getAuth();
  const db = getFirestore();
  const functions = getFunctions(undefined, "us-central1");
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Core state
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userData, setUserData] = useState(null); // includes student + recordings + billingAnchorMs/perStudentCap
  const [loading, setLoading] = useState(true);

  // Range toggle: 'billing' | 'month' | 'all'
  const [rangeMode, setRangeMode] = useState(() => localStorage.getItem("student.rangeMode") || "billing");
  const [activeTab, setActiveTab] = useState(0);
  const [diversityMode, setDiversityMode] = useState(() => localStorage.getItem("diversityMode") === "true");

  // Theme from router (optional) + live theme
  const { theme: routerTheme } = location.state || {};
  const [activeTheme, setActiveTheme] = useState(null);
  const [showThemeDetails, setShowThemeDetails] = useState(true);

  // School/billing context from callable (bypasses rules) — used only as a fallback
  const [schoolCtx, setSchoolCtx] = useState(null); // { orgId, periodStart, periodEnd, perStudentCap, seatLimit, ... }
  const [ctxError, setCtxError] = useState(null);


  useEffect(() => {
    if (routerTheme) setActiveTheme(routerTheme);
  }, [routerTheme]);

  /* ------- Auth listener ------- */
  useEffect(() => {
    const off = onAuthStateChanged(auth, (u) => setFirebaseUser(u || null));
    return () => off();
  }, [auth]);

  /* ------- Load student data ------- */
  useEffect(() => {
    async function fetchStudentData() {
      const u = auth.currentUser;
      if (!u) return;
      try {
        const uid = u.uid;

        // students/{uid}
        const studentSnap = await getDoc(doc(db, "students", uid));
        const student = studentSnap.exists() ? studentSnap.data() || {} : {};

        // users/{uid}
        const userSnap = await getDoc(doc(db, "users", uid));
        const userDoc = userSnap.exists() ? userSnap.data() || {} : {};
        const recordings = Array.isArray(userDoc?.recordings) ? userDoc.recordings : [];

        // NEW: mirrored Stripe subscription window written by webhook/auth-trigger
	const subStartMs = msFromAny(userDoc?.subscription?.currentPeriodStart);
	const subEndMs   = msFromAny(userDoc?.subscription?.currentPeriodEnd);
	const planLabel  = userDoc?.subscription?.displayName || null;


        const enriched = {
  fullName: `${student?.firstName || ""} ${student?.lastName || ""}`.trim() || (userDoc?.fullName || ""),
  email: student?.username ? `${student.username}@yourstoryworld.com` : (userDoc?.email || ""),
  schoolId: student?.schoolId || userDoc?.schoolId || null,
  className: student?.className || null,
  storiesRead: Number(student?.storiesRead || 0),
  totalReadingTime: Number(student?.totalReadingTime || 0), // minutes
  readingActivity: Array.isArray(student?.readingActivity) ? student.readingActivity : [],
  favoriteGenres: student?.genresExplored || student?.favoriteGenres || {},
  comprehensionScores: Array.isArray(student?.comprehensionScores) ? student.comprehensionScores : [],
  recordings,

  // 🔹 Prefer these if present (student-stamped)
  billingAnchorMs: msFromAny(student?.billingAnchorMs) || null,
  perStudentCap: Number(student?.perStudentCap || 0) || null,

  // 🔹 NEW: mirrors from users/{uid}.subscription (webhook/auth-trigger)
  subscriptionStart: subStartMs ? new Date(subStartMs) : null,
  subscriptionEnd:   subEndMs   ? new Date(subEndMs)   : null,
  planLabel,
};

        setUserData(enriched);
      } catch (err) {
        console.error("Error loading student dashboard:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchStudentData();
  }, [auth, db]);

  /* ------- Load current class theme (live) ------- */
  useEffect(() => {
    const schoolId = userData?.schoolId || null;
    const className = userData?.className || null;

    if (!schoolId || !className) {
      setActiveTheme(null);
      return;
    }

    let unsub1 = () => {};
    let unsub2 = () => {};

    const pickNewest = (docs) => {
      const now = Date.now();
      const list = docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((t) => {
          const ms = msFromAny(t?.expiryDate);
          return !ms || ms >= now; // keep if no expiry or not expired yet
        })
        .sort((a, b) => {
          const ax = msFromAny(a?.createdAt) || 0;
          const bx = msFromAny(b?.createdAt) || 0;
          return bx - ax; // newest first
        });
      return list[0] || null;
    };

    try {
      const q1 = query(
        collection(db, "schools", schoolId, "classrooms", className, "themes"),
        orderBy("createdAt", "desc"),
        qlimit(1)
      );
      unsub1 = onSnapshot(
        q1,
        (qs) => {
          const picked = pickNewest(qs.docs);
          if (picked) setActiveTheme(picked);
        },
        (e) => {
          // ignore; fall back below
          console.warn("[student theme q1]", e?.code || e?.message || e);
        }
      );
    } catch (e) {
      console.warn("[student theme q1 init]", e?.message || e);
    }

    try {
      const q2 = query(
        collectionGroup(db, "themes"),
        where("schoolId", "==", schoolId),
        where("classes", "array-contains", className),
        orderBy("createdAt", "desc"),
        qlimit(1)
      );
      unsub2 = onSnapshot(
        q2,
        (qs) => {
          setActiveTheme((cur) => cur || pickNewest(qs.docs));
        },
        (e) => {
          // If you see 'failed-precondition', click the console "Create index" link once.
          console.warn("[student theme q2]", e?.code || e?.message || e);
        }
      );
    } catch (e) {
      console.warn("[student theme q2 init]", e?.message || e);
    }

    return () => {
      try { unsub1(); } catch {}
      try { unsub2(); } catch {}
    };
  }, [db, userData?.schoolId, userData?.className]);

  /* ------- Fetch school context (fallback only) ------- */
    useEffect(() => {
    let cancelled = false;
    async function fetchCtx() {
      setCtxError(null);
      try {
        const callable = httpsCallable(functions, "getSchoolContext");
        const { data } = await callable({ orgId: userData?.schoolId || null });
        if (cancelled) return;

        setSchoolCtx({
          ...data,
          periodStart: data?.periodStart ? new Date(msFromAny(data.periodStart)) : null,
          periodEnd: data?.periodEnd ? new Date(msFromAny(data.periodEnd)) : null,
          perStudentCap: Number(data?.perStudentCap || 40),

          // ✅ keep the anchor so the period can roll forward correctly each month
          billingAnchorMs:
            msFromAny(data?.billingAnchorMs) ??
            msFromAny(data?.orgCreatedAt) ??
            null,
        });
      } catch (e) {
        console.warn("getSchoolContext failed:", e?.message || e);
        if (!cancelled) setCtxError(e?.message || "Unable to load period context");
      }
    }

    if (firebaseUser) fetchCtx();
    return () => { cancelled = true; };
  }, [functions, firebaseUser, userData?.schoolId]);
       

/* ------- Billing window (prefer student.billingAnchorMs) ------- */
const billingWindow = useMemo(() => {
  // 1) Student-stamped anchor → rolling monthly period
  const studentAnchorMs = userData?.billingAnchorMs || null;
  const viaStudentAnchor = currentPeriodFromAnchor(studentAnchorMs);
  if (viaStudentAnchor) return viaStudentAnchor;

  // 2) School anchor → rolling monthly period
  // IMPORTANT:
  // If billingAnchorMs is missing, use the original schoolCtx.periodStart
  // as the anchor (e.g. Dec 15), then roll forward from there.
  const schoolAnchorMs =
    schoolCtx?.billingAnchorMs ??
    msFromAny(schoolCtx?.periodStart) ??
    null;

  const viaSchoolAnchor = currentPeriodFromAnchor(schoolAnchorMs);
  if (viaSchoolAnchor) return viaSchoolAnchor;

  // 3) Mirrored user subscription window (unlikely for students, but harmless fallback)
  if (userData?.subscriptionStart && userData?.subscriptionEnd) {
    return {
      start: userData.subscriptionStart,
      end: userData.subscriptionEnd,
    };
  }

  // 4) Calendar month fallback only
  const now = new Date();
  return {
    start: startOfCalendarMonth(now),
    end: endOfCalendarMonth(now),
  };
}, [
  userData?.billingAnchorMs,
  userData?.subscriptionStart,
  userData?.subscriptionEnd,
  schoolCtx?.billingAnchorMs,
  schoolCtx?.periodStart,
]);

  const calendarWindow = useMemo(() => {
    const now = new Date();
    return { start: startOfCalendarMonth(now), end: endOfCalendarMonth(now) };
  }, []);

  function getCurrentWindow() {
    if (rangeMode === "billing") return billingWindow;
    if (rangeMode === "month") return calendarWindow;
    return { start: null, end: null }; // all time
  }


console.log("STUDENT BILLING DEBUG", {
  studentBillingAnchor: userData?.billingAnchorMs,
  schoolBillingAnchor: schoolCtx?.billingAnchorMs,
  subscriptionStart: userData?.subscriptionStart,
  subscriptionEnd: userData?.subscriptionEnd,
  chosenStart: billingWindow?.start,
  chosenEnd: billingWindow?.end,
});

  /* ------- Cap & usage ------- */
  const perStudentCap =
    Number(userData?.perStudentCap || 0) ||
    Number(schoolCtx?.perStudentCap || 0) ||
    40;

  const { start: rangeStart, end: rangeEnd } = getCurrentWindow();

  const storiesGeneratedThisRange = useMemo(() => {
    return sumStoriesInRange(userData?.readingActivity || [], rangeStart, rangeEnd);
  }, [userData, rangeStart, rangeEnd]);


  const totalReadingTimeThisRange = useMemo(() => {
    const arr = Array.isArray(userData?.readingActivity) ? userData.readingActivity : [];
    return arr.reduce((sum, e) => {
      const when = e?.date || e?.ts || null;
      const seconds = Number(e?.timeSpent || 0);
      if (!rangeStart && !rangeEnd) return sum + seconds;
      return inRange(when, rangeStart, rangeEnd) ? sum + seconds : sum;
    }, 0);
  }, [userData, rangeStart, rangeEnd]);

  const averageReadingTimeThisRange = useMemo(() => {
    const stories = storiesGeneratedThisRange || 0;
    return stories > 0 ? Math.round(totalReadingTimeThisRange / stories / 60) : 0;
  }, [storiesGeneratedThisRange, totalReadingTimeThisRange]);

  const mostPopularGenre = useMemo(() => {
    const g = userData?.favoriteGenres || {};
    const entries = Object.entries(g).filter(([k, v]) => k !== "0" && typeof v === "number" && v > 0);
    if (!entries.length) return "—";
    entries.sort((a, b) => b[1] - a[1]);
    return entries[0][0] || "—";
  }, [userData]);

  // Used in THIS BILLING PERIOD (for gating)
  const usedThisBilling = useMemo(() => {
    return sumStoriesInRange(userData?.readingActivity || [], billingWindow.start, billingWindow.end);
  }, [userData, billingWindow]);
  const remainingThisBilling = Math.max(perStudentCap - usedThisBilling, 0);


  // Comprehension aggregates
  const weeklyComp = useMemo(() => avgByWeek(userData?.comprehensionScores || []), [userData?.comprehensionScores]);
 

  /* ------- Charts ------- */
  const last7Days = useMemo(() => {
    const arr = Array.from({ length: 7 }, (_, index) => {
      const d = new Date();
      d.setDate(d.getDate() - index);
      return d.toISOString().split("T")[0];
    }).reverse();
    return arr;
  }, []);

  const last6Weeks = useMemo(() => {
    const arr = Array.from({ length: 6 }, (_, index) => {
      const today = new Date();
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay() - index * 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return {
        label: `${start.getDate()} ${start.toLocaleString("default", {
          month: "short",
        })} - ${end.getDate()} ${end.toLocaleString("default", {
          month: "short",
        })}`,
        start,
        end,
      };
    }).reverse();
    return arr;
  }, []);

  const storiesReadData = useMemo(
    () => ({
      labels: last7Days,
      datasets: [
        {
          label: "Stories Read Per Day",
          data: last7Days.map((d) => {
            const e = (userData?.readingActivity || []).find((x) => x.date === d);
            return e ? e.storiesGenerated || 0 : 0;
          }),
          backgroundColor: "rgba(0, 123, 255, 0.5)",
          borderColor: "rgba(0, 123, 255, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [last7Days, userData]
  );

  const timeSpentReadingData = useMemo(
    () => ({
      labels: last7Days,
      datasets: [
        {
          label: "Total Reading Time Per Day (Minutes)",
          data: last7Days.map((d) => {
            const e = (userData?.readingActivity || []).find((x) => x.date === d);
            return e ? Math.round((e.timeSpent || 0) / 60) : 0;
          }),
          backgroundColor: "rgba(255, 99, 132, 0.5)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [last7Days, userData]
  );

  const weeklyReadingData = useMemo(() => {
    const readingActivity = Array.isArray(userData?.readingActivity) ? userData.readingActivity : [];
    return last6Weeks.map((w) =>
      readingActivity.filter((e) => {
        const d = new Date(e.date);
        return d >= w.start && d <= w.end;
      }).length || 0
    );
  }, [last6Weeks, userData]);

  const readingStreaksData = useMemo(
    () => ({
      labels: last6Weeks.map((w) => w.label),
      datasets: [
        {
          label: "Reading Streak (Days per Week)",
          data: weeklyReadingData,
          backgroundColor: "rgba(255, 165, 0, 0.6)",
          borderColor: "rgba(255, 165, 0, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [last6Weeks, weeklyReadingData]
  );

  const favoriteGenresData = useMemo(
    () => ({
      labels: Object.keys(userData?.favoriteGenres || {}),
      datasets: [
        {
          label: userData?.fullName || (userData?.email || "").split("@")[0] || "You",
          data: Object.values(userData?.favoriteGenres || {}),
          backgroundColor: "rgba(0, 123, 255, 0.6)",
          borderColor: "rgba(0, 123, 255, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [userData]
  );

  const averageReadingTimeData = useMemo(
    () => ({
      labels: last7Days,
      datasets: [
        {
          label: "Average Reading Time Per Session (Minutes)",
          data: last7Days.map((d) => {
            const e = (userData?.readingActivity || []).find((x) => x.date === d);
            if (e && e.storiesGenerated > 0) {
              return Math.round((e.timeSpent || 0) / e.storiesGenerated / 60);
            }
            return 0;
          }),
          backgroundColor: "rgba(0, 128, 0, 0.5)",
          borderColor: "rgba(0, 128, 0, 1)",
          borderWidth: 2,
        },
      ],
    }),
    [last7Days, userData]
  );

  const comprehensionScoresData = useMemo(
    () => ({
      labels: (userData?.comprehensionScores || []).map((e) => {
        const d = new Date(e.date);
        return `${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
      }),
      datasets: [
        {
          label: "Comprehension Score (%)",
          data: (userData?.comprehensionScores || []).map((e) => e.score || 0),
          backgroundColor: "rgba(153, 102, 255, 0.5)",
          borderColor: "rgba(153, 102, 255, 1)",
          borderWidth: 2,
          fill: false,
        },
      ],
    }),
    [userData]
  );
  // NEW: Weekly averaged comprehension mini-series
  const comprehensionWeeklyAvgData = useMemo(() => {
    const weekly = weeklyComp;
    return {
      labels: weekly.map(w => w.week),
      datasets: [
        {
          label: "Comprehension (Weekly Avg %)",
          data: weekly.map(w => w.avg),
          backgroundColor: "rgba(54, 162, 235, 0.5)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 2,
          fill: false,
        }
      ]
    };
  }, [weeklyComp]);

  /* ------- Handlers ------- */
  const outOfCreditsNotice = () => {
    const resetMsg = billingWindow?.end ? ` Your limit resets on ${formatDate(billingWindow.end)}.` : "";
    const poolMsg = " Ask your teacher or school admin—if your school has unused capacity, they can assign extra stories to you.";
    toast.error(`You’ve used your story limit for this billing period.${resetMsg}${poolMsg}`);
  };

  const handleGenerateStory = () => {
    if (remainingThisBilling <= 0) {
      outOfCreditsNotice();
      return;
    }
    navigate(
      `/storyapp?profile=${encodeURIComponent(userData?.fullName || userData?.email || "Reader")}&diversity=${diversityMode}`
    );
  };

  const handleCreateStoryWithTheme = () => {
    if (remainingThisBilling <= 0) {
      outOfCreditsNotice();
      return;
    }
    navigate(
      `/storyapp?profile=${encodeURIComponent(userData?.fullName || userData?.email || "Reader")}&diversity=${diversityMode}`,
      { state: { theme: activeTheme } }
    );
  };

  const handleDeleteRecording = async (rec) => {
    try {
      const storage = getStorage();
      const user = auth.currentUser;
      if (!user) return;

      // Delete file from Storage
      const path = rec.storagePath || `recordings/${user.uid}/${rec.fileName}`;
      await deleteObject(ref(storage, path));

      // Remove from users/{uid}.recordings
      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        const data = snap.data() || {};
        const updated = (Array.isArray(data.recordings) ? data.recordings : []).filter(
          (r) => r.fileName !== rec.fileName
        );
        await updateDoc(userRef, { recordings: updated });
        setUserData((prev) => ({ ...prev, recordings: updated }));
      }

      toast.success("Recording deleted.");
    } catch (error) {
      console.error("Failed to delete recording:", error);
      toast.error("Something went wrong while deleting the recording.");
    }
  };

  const handleSendToTeacher = async (recording) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const uid = user.uid;

      // Update users/{uid}.recordings: sentToTeacher = true
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        toast.error("User profile not found.");
        return;
      }
      const udata = userSnap.data() || {};
      const updatedUserRecordings = (Array.isArray(udata.recordings) ? udata.recordings : []).map((r) =>
        r.fileName === recording.fileName ? { ...r, sentToTeacher: true } : r
      );
      await updateDoc(userRef, { recordings: updatedUserRecordings });

      // Mirror to students/{uid}.recordings (best-effort)
      const studentRef = doc(db, "students", uid);
      const studentSnap = await getDoc(studentRef);
      const sdata = studentSnap.exists() ? studentSnap.data() || {} : {};
      const currentStudentRecs = Array.isArray(sdata.recordings) ? sdata.recordings : [];

      const idx = currentStudentRecs.findIndex((r) => r.fileName === recording.fileName);
      const payload = {
        ...recording,
        sentToTeacher: true,
        storyTitle: recording.storyTitle || recording.fileName,
        uploadedAt: recording.uploadedAt || Date.now(),
      };
      const nextStudentRecs =
        idx >= 0
          ? Object.assign([...currentStudentRecs], { [idx]: { ...currentStudentRecs[idx], ...payload } })
          : [...currentStudentRecs, payload];

      await updateDoc(studentRef, { recordings: nextStudentRecs });

      // Update local UI
      setUserData((prev) => ({ ...prev, recordings: updatedUserRecordings }));
      toast.success("Recording sent to your teacher!");
    } catch (err) {
      console.error("Failed to send recording:", err);
      toast.error("Something went wrong while sending the recording.");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUserData(null);
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  /* ------- Early returns ------- */
  if (loading) return <p>Loading student dashboard...</p>;
  if (!userData) return <p>Student data not found.</p>;

  // Nice display: show end as previous day (end is exclusive)
  const displayEnd = billingWindow?.end ? new Date(billingWindow.end.getTime() - 24 * 60 * 60 * 1000) : null;


  /* ------- UI ------- */
  return (
  <div
    className="page-container"
    style={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      overflow: "auto",              // ✅ allow vertical scrolling
      width: "100%",                 // ✅ avoid weird clipping on mobile
      backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
      backgroundSize: "cover",
      backgroundPosition: "center center",
      backgroundAttachment: isMobile ? "scroll" : "fixed", // ✅ like FreeDashboard
      fontFamily: "Montserrat, sans-serif",
    }}
  >

      {/* Header */}
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: "15px", justifyContent: "flex-end" }}>
          <button onClick={() => navigate("/help")} className="header-button">
            Help
          </button>
          <button onClick={() => navigate("/")} className="header-button">
            Home
          </button>
          <button
            onClick={handleLogout}
            className="header-button"
            style={{ backgroundColor: "blue", color: "white", fontWeight: "bold" }}
          >
            Logout
          </button>
        </nav>
      </header>

      {/* Main Content */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          padding: "20px",
          gap: "20px",
          alignItems: "flex-start",
          flexWrap: "wrap",
          overflowX: "auto",
        }}
      >
        {/* Sidebar */}
        <aside
          style={{
            width: isMobile ? "100%" : "280px",
            background: "rgba(230, 245, 255, 0.95)",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div>
            <h2 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", marginBottom: "6px" }}>
              {userData?.fullName || userData?.email || "User"}
            </h2>

            
            {/* Story usage for THIS BILLING PERIOD */}
            <div
              style={{
                marginTop: 8,
                background: "#f6f7fb",
                padding: 12,
                borderRadius: 8,
                border: "1px solid #e5e7eb",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>📚 Story Limit (This Month)</div>
              <div style={{ fontSize: 14 }}>
                Used / Cap: <strong>{usedThisBilling}</strong> / <strong>{perStudentCap}</strong>
              </div>
              <div style={{ fontSize: 14 }}>
                Remaining: <strong>{remainingThisBilling}</strong>
              </div>

              {/* Progress bar */}
              <div
                style={{
                  height: 8,
                  background: "#e0e7ff",
                  borderRadius: 999,
                  overflow: "hidden",
                  marginTop: 8,
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, Math.round((usedThisBilling / Math.max(1, perStudentCap)) * 100))}%`,
                    height: "100%",
                    background: remainingThisBilling > 0 ? "#3b82f6" : "#ef4444",
                  }}
                />
              </div>

              {/* Billing period dates (end shown as previous day) */}
              <div style={{ fontSize: 12, marginTop: 8, color: "#334155" }}>
                Period:&nbsp;
                <strong>
                  {formatDate(billingWindow.start)} → {formatDate(displayEnd)}
                </strong>
              </div>

              {/* reset notice */}
              <div style={{ fontSize: 12, marginTop: 4, color: "#334155" }}>
                <strong>Your story limit resets on {formatDate(billingWindow.end)}.</strong>
              </div>

              {ctxError && !userData?.billingAnchorMs && (
                <div style={{ marginTop: 8, color: "#9a3412" }}>
                  ⚠︎ Couldn’t load school billing window; showing calendar month.
                </div>
              )}
            </div>
          </div>

          {/* Diversity Mode Toggle */}
          <div style={{ marginTop: 8, background: "#f4f4f4", padding: "10px", borderRadius: "8px" }}>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: "10px", fontSize: "14px" }}>
              <input
                type="checkbox"
                checked={diversityMode}
                onChange={() => {
                  const v = !diversityMode;
                  setDiversityMode(v);
                  localStorage.setItem("diversityMode", String(v));
                }}
              />
              <span>Enable Diversity Mode</span>
            </label>
            <p style={{ fontSize: "12px", color: "#555", marginTop: "6px" }}>
              Make characters, settings and themes reflect diverse cultures and backgrounds.
            </p>
          </div>
        </aside>

        {/* Main Dashboard Area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontFamily: "Montserrat, sans-serif", color: "#007BFF", textAlign: "center" }}>
            Welcome to Your Dashboard
          </h1>

         
          {/* Theme card (optional) */}
          {activeTheme && (
            <div
              style={{
                background: "linear-gradient(to right, #FFEFBA, #FFFFFF)",
                borderLeft: "6px solid #FF9800",
                padding: "20px",
                borderRadius: "10px",
                marginBottom: "25px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ margin: 0, color: "#D84315" }}>📚 This Week’s Theme</h2>
                <button
                  onClick={() => setShowThemeDetails((p) => !p)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#007BFF",
                    fontSize: "14px",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {showThemeDetails ? "Hide Details" : "Show Details"}
                </button>
              </div>
              {showThemeDetails && (
                <div style={{ marginTop: 10 }}>
                  <p><strong>Theme:</strong> {activeTheme.theme}</p>
                  <p><strong>Setting:</strong> {activeTheme.setting}</p>
                  <p><strong>Description:</strong> {activeTheme.description}</p>
                  {activeTheme.comment && (
                    <p
                      style={{
                        backgroundColor: "#fff3cd",
                        padding: "10px",
                        borderRadius: "6px",
                        color: "#856404",
                        marginTop: "10px",
                      }}
                    >
                      <strong>Teacher’s Message:</strong> {activeTheme.comment}
                    </p>
                  )}
                  <button
                    onClick={handleCreateStoryWithTheme}
                    style={{
                      marginTop: 10,
                      backgroundColor: remainingThisBilling <= 0 ? "#9CA3AF" : "#007BFF",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      cursor: remainingThisBilling <= 0 ? "not-allowed" : "pointer",
                      fontWeight: "bold",
                    }}
                    disabled={remainingThisBilling <= 0}
                    title={remainingThisBilling <= 0 ? "No story credits left this billing period, your teacher can further if required before the next period." : undefined}
                  >
                    ✏️ Start a Story with This Theme
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Generate Story Card */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              padding: "30px",
              borderRadius: "10px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            <h2>Click the button below to start creating your stories!</h2>
            <p style={{ marginTop: 6 }}>
              Stories left this billing period: <strong>{remainingThisBilling}</strong> of{" "}
              <strong>{perStudentCap}</strong>
            </p>
            <button
              onClick={handleGenerateStory}
              style={{
                padding: "15px 30px",
                backgroundColor: remainingThisBilling <= 0 ? "#9CA3AF" : "#007BFF",
                color: "white",
                border: "none",
                borderRadius: "5px",
                fontSize: "1rem",
                cursor: remainingThisBilling <= 0 ? "not-allowed" : "pointer",
                marginTop: "10px",
              }}
              disabled={remainingThisBilling <= 0}
              title={remainingThisBilling <= 0 ? "No story credits left this billing period, your teacher can further if required before the next period." : undefined}
            >
              Create a Story
            </button>
          </div>

          {/* Reading Summary (respects rangeMode) */}
          <div
            className="reading-summary"
            style={{
              background: "linear-gradient(to right, #2C3E50, #4CA1AF)",
              padding: 20,
              borderRadius: 10,
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              textAlign: "center",
              marginBottom: 20,
              color: "white",
            }}
          >
            {/* Range buttons */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className={`subscription-button ${rangeMode === "billing" ? "active" : ""}`}
                  onClick={() => {
                    setRangeMode("billing");
                    localStorage.setItem("student.rangeMode", "billing");
                  }}
                  title="Show stats for your current billing period."
                  style={{ background: rangeMode === "billing" ? "#0ea5e9" : undefined }}
                >
                  Billing Period
                </button>
                <button
                  className={`subscription-button ${rangeMode === "month" ? "active" : ""}`}
                  onClick={() => {
                    setRangeMode("month");
                    localStorage.setItem("student.rangeMode", "month");
                  }}
                  title="Show stats since the 1st of this calendar month."
                  style={{ background: rangeMode === "month" ? "#0ea5e9" : undefined }}
                >
                  This Month
                </button>
                <button
                  className={`subscription-button ${rangeMode === "all" ? "active" : ""}`}
                  onClick={() => {
                    setRangeMode("all");
                    localStorage.setItem("student.rangeMode", "all");
                  }}
                  title="Show all-time totals."
                  style={{ background: rangeMode === "all" ? "#0ea5e9" : undefined }}
                >
                  All Time
                </button>
              </div>
              <div style={{ fontSize: 12, opacity: 0.9, marginTop: 8 }}>
                Summary and charts follow your selection.
              </div>
            </div>

            <h2>📊 Reading Summary</h2>
            <p style={{ fontSize: 14, opacity: 0.9 }}>
              Currently showing:{" "}
              <strong>
                {rangeMode === "billing" ? "Billing Period" : rangeMode === "month" ? "This Month" : "All Time"}
              </strong>
            </p>
            <div
              style={{ display: "flex", justifyContent: "space-around", marginTop: 10, flexWrap: "wrap", gap: 16 }}
            >
              <div>
                <h3 style={{ color: "#F9C74F", fontSize: "1.8rem" }}>📖 {storiesGeneratedThisRange}</h3>
                <p>Stories Generated</p>
              </div>
              <div>
                <h3 style={{ color: "#F94144", fontSize: "1.8rem" }}>
                  ⏳ {Math.round(totalReadingTimeThisRange / 60)} min
                </h3>
                <p>Total Reading Time</p>
              </div>
              <div>
                <h3 style={{ color: "#90BE6D", fontSize: "1.8rem" }}>
                  📊 {averageReadingTimeThisRange} min
                </h3>
                <p>Avg. Reading Time</p>
              </div>
              <div>
                <h3 style={{ color: "#F3722C", fontSize: "1.8rem" }}>🌟 {mostPopularGenre}</h3>
                <p>Most Popular Genre</p>
              </div>
            </div>
          </div>

         {/* Tabs */}
<div
  style={{
    background: "linear-gradient(to right, #2C3E50, #4CA1AF)",
    padding: 15,
    borderRadius: 10,
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    marginBottom: 20,
  }}
>
  <Tabs
    value={activeTab}
    onChange={(event, newValue) => setActiveTab(newValue)}
    indicatorColor="primary"
    textColor="primary"
    centered={!isMobile}
    variant={isMobile ? "scrollable" : "standard"}
    scrollButtons={isMobile ? "auto" : false}
    allowScrollButtonsMobile
    sx={{
      "& .MuiTabs-flexContainer": { justifyContent: "center" },
      "& .MuiTab-root": {
        fontWeight: "bold",
        fontSize: "16px",
        color: "white",
        textTransform: "none",
      },
      "& .Mui-selected": {
        color: "#007BFF !important",
      },
    }}
  >
    <Tab label="📈 Progress Overview" />
    <Tab label="🔥 Reading Habits" />
    <Tab label="⏳ Reading/Comprehension Insights" />
    <Tab label="🎙️ Recordings" />
  </Tabs>
</div>


          {/* Charts per tab */}
          <div style={{ marginTop: "20px" }}>
            {activeTab === 0 && (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "20px" }}>
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: "14px", marginBottom: "5px" }}>
                    📈 Daily Stories Generated <span title="Shows number of stories created each day." style={{ cursor:"help", fontWeight:700 }}>ⓘ</span>
                  </h2>
                  <p style={{ color: "#555", fontSize: "12px", marginBottom: "5px" }}>
                    Last 7 days
                  </p>
                  <div style={{ height: "220px", position: "relative" }}>
                    <Bar data={storiesReadData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </div>

                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: "14px", marginBottom: "5px" }}>
                    🔥 Total Reading Time <span title="Shows total minutes spent reading per day." style={{ cursor:"help", fontWeight:700 }}>ⓘ</span>
                  </h2>
                  <p style={{ color: "#555", fontSize: "12px", marginBottom: "5px" }}>
                    Last 7 days
                  </p>
                  <div style={{ height: "220px", position: "relative" }}>
                    <Line data={timeSpentReadingData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 1 && (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "20px" }}>
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: "14px", marginBottom: "5px" }}>
                    🔥 Days read per week <span title="Shows how many days you read in each week." style={{ cursor:"help", fontWeight:700 }}>ⓘ</span>
                  </h2>
                  <p style={{ color: "#555", fontSize: "12px", marginBottom: "5px" }}>
                    Last 6 weeks
                  </p>
                  <div style={{ height: "220px", position: "relative" }}>
                    <Bar
                      data={readingStreaksData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: { title: { display: true, text: "Days Read (0-7)" }, beginAtZero: true, max: 7 },
                          x: { title: { display: true, text: "Week" } },
                        },
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: "14px", marginBottom: "5px" }}>🌟 Favorite Genres</h2>
                  <p style={{ color: "#555", fontSize: "12px", marginBottom: "5px" }}>
                    All-time counts
                  </p>
                  <div style={{ height: "220px", position: "relative" }}>
                    <Bar data={favoriteGenresData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 2 && (
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "20px" }}>
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: "14px", marginBottom: "5px" }}>⏳ Average Reading Time</h2>
                  <p style={{ color: "#555", fontSize: "12px", marginBottom: "5px" }}>
                    Last 7 days
                  </p>
                  <div style={{ height: "220px", position: "relative" }}>
                    <Line data={averageReadingTimeData} options={{ responsive: true, maintainAspectRatio: false }} />
                  </div>
                </div>

                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: "14px", marginBottom: "5px" }}>🧠 Comprehension Scores</h2>
                  <p style={{ color: "#555", fontSize: "12px", marginBottom: "5px" }}>
                    All-time
                  </p>
                  <div style={{ height: "220px", position: "relative" }}>
                    <Line
                      data={comprehensionScoresData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: { title: { display: true, text: "Score (%)" }, beginAtZero: true, max: 100 },
                          x: { title: { display: true, text: "Date" } },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            {/* Mini weekly comprehension line (same tab 2) */}
            {activeTab === 2 && (
              <div style={{ marginTop: 20 }}>
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.9)",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    height: "260px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <h2 style={{ fontSize: "14px", marginBottom: "5px" }}>
                    📈 Comprehension Progress (Weekly Avg) <span title="Average quiz accuracy per week (0–100%)." style={{ cursor:"help", fontWeight:700 }}>ⓘ</span>
                  </h2>
                  <p style={{ color: "#555", fontSize: "12px", marginBottom: "5px" }}>
                    Recent weeks
                  </p>
                  <div style={{ height: "180px", position: "relative" }}>
                    <Line
                      data={comprehensionWeeklyAvgData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: { title: { display: true, text: "Avg Score (%)" }, beginAtZero: true, max: 100 },
                          x: { title: { display: true, text: "Week (Mon start)" } },
                        },
                        elements: { point: { radius: 2 } }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 3 && (
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  padding: "20px",
                  borderRadius: "10px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
              >
                <h2 style={{ fontFamily: "Montserrat, sans-serif", marginBottom: "10px" }}>
                  🎧 Saved Read-Aloud Recordings
                </h2>

                {!userData?.recordings || userData.recordings.length === 0 ? (
                  <div style={{ textAlign: "center", marginTop: "30px" }}>
                    <p style={{ color: "#777", marginBottom: "20px" }}>
                      No recordings saved yet. Record yourself reading a story to see them here!
                    </p>
                  </div>
                ) : (
                  <>
                    {userData.recordings.map((rec, idx) => (
                      <div key={idx} style={{ marginBottom: "15px", borderBottom: "1px solid #ccc", paddingBottom: "10px" }}>
                        <p>
                          <strong>{rec.storyTitle || rec.fileName}</strong>
                        </p>
                        <p style={{ fontSize: "0.9em", color: "#555" }}>
                          Uploaded: {(rec.uploadedAt || rec.createdAt)
                            ? new Date(rec.uploadedAt || rec.createdAt).toLocaleString()
                            : "Unknown"}
                        </p>
                        <audio controls src={rec.url} style={{ width: "100%", marginTop: "8px" }} />

                        <button
                          onClick={() => handleDeleteRecording(rec)}
                          style={{
                            marginTop: "10px",
                            backgroundColor: "#ff4d4d",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "5px",
                            border: "none",
                            cursor: "pointer",
                            marginRight: "10px",
                          }}
                        >
                          🗑️ Delete
                        </button>

                        {!rec.sentToTeacher && (
                          <button
                            onClick={() => handleSendToTeacher(rec)}
                            style={{
                              marginTop: "10px",
                              backgroundColor: "#4CAF50",
                              color: "white",
                              padding: "6px 12px",
                              borderRadius: "5px",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            📤 Send to Teacher
                          </button>
                        )}
                        {rec.sentToTeacher && (
                          <p style={{ marginTop: "10px", color: "#4CAF50", fontWeight: "bold" }}>✅ Sent to Teacher</p>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="page-footer">
        <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
        <nav style={{ marginTop: "10px" }}>
          <a href="/about" className="footer-link">
            About Us
          </a>
          <a href="/contact" className="footer-link">
            Contact
          </a>
        </nav>
      </footer>
    </div>
  );
};

export default StudentDashboard;

